import {
  Box,
  Typography as Typo,
  Card as MuiCard,
  styled,
  TypographyProps,
  Chip as MuiChip,
  FormControl as MuiFormControl,
  FormHelperText as MuiFormHelperText,
  Skeleton,
  Grid,
} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useFormik} from 'formik';
import {Button, Input, VersionVariants} from '@jobseeker/lotus/elements';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {saveUserSettings} from '@jobseeker/common-core/reducers/user/thunkServices';
import {validationSchemaBlockedCompanies} from '@jobseeker/feature-profile/helpers/formValidator';
import userService from '@jobseeker/common-core/services/user.service';
import {enqueueSnackbar} from 'notistack';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import Checkbox from '@jobseeker/lotus/elements/CheckBox';
import {
  BlankTickHirist,
  CheckHirist,
  CrossHirist,
} from '@jobseeker/lotus/assets/icons';
import Loader from '@jobseeker/lotus/components/Loader';

interface BlockedCompany {
  name: string;
  id: number;
}

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
  bottom: -18,
}));

const CustomFormControl = styled(MuiFormControl)(() => ({
  '& .MuiInputBase-root': {
    '&:focus': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
    '&:hover': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
    },
    '& textarea:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
    },
    '& select:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
    },
    '&.Mui-error': {
      border: 'none',
      borderRadius: 'inherit',
    },
  },
  '& .MuiInputBase-input': {
    height: '100%',
    outline: 'none !important',
    boxShadow: 'none !important',
    '&:focus': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
    '&:hover': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
  },
}));

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));

const CustomChip = styled(MuiChip)(() => ({
  background: '#f5f5f5',
  padding: '11px 12px 11px 4px',
}));

const Typography = styled(Typo)<TypographyProps>(({}) => ({
  display: 'flex',
  alignItems: 'center',
}));

const BlockCompaniesComponent: React.FC<{
  isLarge: boolean;
  setCurrentOrg: React.Dispatch<React.SetStateAction<string>>;
}> = ({isLarge, setCurrentOrg}) => {
  const [items, setItems] = useState<BlockedCompany[]>([]);

  useEffect(() => {
    const fetchBlockedCompanies = async () => {
      try {
        const response = await userService.getBlockedCompanies();

        if (response.error) {
          enqueueSnackbar(response.error, {variant: 'error'});
          return;
        }
        const newData = response?.data?.blockedCompanies?.map(
          (item: BlockedCompany) => ({
            name: item.name,
            id: item.id,
          })
        );
        setItems(newData);
        setCurrentOrg(response?.data?.currentOrganization || '');
      } catch (error) {
        console.log(error);
      }
    };

    fetchBlockedCompanies();
  }, [setCurrentOrg]);

  const handleBlockCompany = async (
    name: string,
    action: 'block' | 'unblock'
  ) => {
    const response = await userService.blockOrUnblockCompany({
      name,
      action,
    });
    if (response.error) {
      enqueueSnackbar(response.error, {variant: 'error'});
      return;
    }
    const obj = response.data;
    if (action === 'block') {
      setItems(prevItems => [...prevItems, obj]);
      enqueueSnackbar(response?.status?.message, {variant: 'success'});
    } else {
      setItems(prevItems => prevItems.filter(comp => comp.id != obj.id));
      enqueueSnackbar(response?.status?.message, {variant: 'success'});
    }
    console.log(obj);
  };

  const formik = useFormik({
    initialValues: {
      blockedCompany: '',
    },
    validationSchema: validationSchemaBlockedCompanies,
    onSubmit: values => {
      handleBlockCompany(values.blockedCompany, 'block');
      formik.resetForm();
    },
  });

  const hasError =
    formik.touched.blockedCompany && Boolean(formik.errors.blockedCompany);

  return (
    <Box
      data-testid="notification_list_item"
      sx={{
        width: '100%',
        position: 'relative',
        transition: 'all ease 0.2s',
      }}
    >
      <Grid
        container
        spacing={isLarge ? 4.375 : 5}
        // sx={{paddingX: 2.5, paddingBottom: 4.625, paddingTop: 3}}
      >
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              ...(isLarge && {border: '1px solid #ddd'}),
              borderRadius: 2,
              padding: isLarge ? '25px 20px' : 0,
              position: 'relative',
              maxWidth: 600,
              margin: 'auto',
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                position: 'absolute',
                top: isLarge ? -12 : -20,
                left: isLarge ? 16 : 0,
                backgroundColor: 'white',
                padding: isLarge ? '0 8px' : '0px',
                fontWeight: 'bold',
              }}
            >
              Search Companies
            </Typography>

            <Box display="flex" mb={2}>
              <Box
                mt={isLarge ? 0 : 2}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  height: '56px',
                  width: '100%',
                }}
                component={'form'}
                noValidate
                onSubmit={event => {
                  event.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <CustomFormControl error={hasError} sx={{width: '100%'}}>
                  <Input
                    variant={VersionVariants.VERSION_ONE}
                    id="blockedCompany"
                    size="small"
                    type="text"
                    name="blockedCompany"
                    placeholder="Please enter the domain name"
                    value={formik.values.blockedCompany}
                    onChange={formik.handleChange}
                    sx={{
                      marginTop: '0px',
                      width: '100%',
                      '& .MuiInputBase-root': {
                        height: '56px',
                      },
                      '& .MuiInputBase-input': {
                        height: '100%',
                        '&::placeholder': {
                          fontSize: '16px',
                          '@media (max-width:1024px)': {
                            fontSize: '12px',
                          },
                        },
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ccc',
                        },
                        '&:hover fieldset': {
                          borderColor: '#ccc',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: {xxs: '#E59700', md: '#000'},
                        },
                      },
                    }}
                    error={hasError}
                  />
                  {hasError && (
                    <FormHelperText error>
                      {formik.errors.blockedCompany}
                    </FormHelperText>
                  )}
                </CustomFormControl>

                <Box display="flex" flexWrap="nowrap" alignItems="center">
                  <Button
                    disabled={hasError}
                    variant="contained"
                    label={'block_company'}
                    size="small"
                    data-testid="block_company"
                    sx={{
                      color: '#fff',
                      fontSize: '14px',
                      backgroundColor: '#e9630c',
                      px: {xxs: 3, md: 3, lg: 4},
                      py: 2,
                      height: 'fit-content',
                      fontWeight: '500',
                      lineHeight: '24px',
                      '&.Mui-disabled': {
                        backgroundColor: '#e79e00',
                        color: '#fff',
                        border: 'none',
                      },
                      '&:hover': {
                        backgroundColor: '#e9630c',
                      },
                    }}
                    type="submit"
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
            </Box>

            <Typography
              variant="body2"
              fontSize={13}
              color="#000"
              sx={{opacity: 0.6}}
            >
              You may now block certain companies from viewing your profile.
              Please enter the domain name (ex: ibm.com) of companies you wish
              to block. Any recruiter with email-address from this domain name
              (ex: xyz@ibm.com) will not be able to view your profile. You may
              block as many companies as you want.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: '100%',
              border: '1px solid #ddd',
              borderRadius: 2,
              paddingBottom: 2,
              position: 'relative',
              maxWidth: 600,
              margin: 'auto',
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                position: 'absolute',
                top: -12,
                left: 16,
                backgroundColor: 'white',
                padding: '0 8px',
                fontWeight: 'bold',
              }}
            >
              Blocked Companies
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                padding: 2,
              }}
            >
              {items?.length === 0 && (
                <Typography
                  mt={2}
                  ml={1}
                  variant="body2"
                  fontSize={13}
                  color="secondary.dark"
                >
                  No blocked companies
                </Typography>
              )}
              {items?.map((f, i) => (
                <CustomChip
                  sx={{margin: 0.5}}
                  deleteIcon={
                    <Image
                      src={CrossHirist}
                      alt={'Close Icon'}
                      width={10}
                      height={10}
                      objectFit="cover"
                      style={{cursor: 'pointer'}}
                    />
                  }
                  key={`filterchip-filter-${i}`}
                  label={f.name}
                  data-testid="blocked-companies"
                  onDelete={async () => {
                    await handleBlockCompany(f.name, 'unblock');
                  }}
                />
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const PrivacySettingWidget = (props: {loading: boolean}) => {
  const {loading} = props;

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const userSettings = useSelector(
    (state: AppState) => state.user.profile.settings
  );
  const isLarge = useIsLargeDevice();
  const dispatch = useDispatch();

  const [hideResume, setHideResume] = useState(userSettings.hideResume || 0);
  const [currentOrg, setCurrentOrg] = useState<string>('');

  const saveSettings = (settings: {[key: string]: number}) => {
    try {
      setButtonLoading(true);

      dispatch(saveUserSettings(settings));

      enqueueSnackbar('Settings have been saved successfully.', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('Failed to save settings.', {variant: 'error'});
    } finally {
      setButtonLoading(false);
    }
  };

  const handleSaveSettings = () => {
    saveSettings({
      hideResume: hideResume,
    });
  };

  return (
    <Box px={{xxs: 1.25, md: 0}}>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          marginLeft: {md: 0.625},
          width: '100%',
          px: {xxs: 1.875, md: 4.625},
          pb: {xxs: 1.875, md: 4.625},
          height: '100%',
        }}
        data-test-id="privacy_setting_container"
      >
        <Box>
          {isLarge && (
            <Box display={'flex'} justifyContent={'space-between'}>
              <Typography
                display="flex"
                fontSize={{xxs: 16, md: 20}}
                fontWeight={400}
                m={0}
                mb={1}
                mt={2.75}
                sx={{
                  wordWrap: 'break-word',
                  textTransform: 'capitalize',
                }}
              >
                Privacy Settings
              </Typography>
              {currentOrg.length > 0 && (
                <Typography
                  display="flex"
                  fontStyle="italic"
                  fontSize={14}
                  fontWeight={300}
                  mb={1}
                  mt={3}
                  sx={{
                    borderRadius: 0.75,
                    padding: '5px 12px',
                    border: '1px solid #fbf0dd',
                    backgroundColor: '#fffcf7',
                    wordWrap: 'break-word',
                    textTransform: 'capitalize',
                  }}
                >
                  Your current organisation is&nbsp;
                  <Box component="span" fontWeight={700} fontStyle={'normal'}>
                    {currentOrg}
                  </Box>
                </Typography>
              )}
            </Box>
          )}

          <Box mt={isLarge ? 0 : 1.875}>
            {loading ? (
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                px={{xxs: 1, md: 2}}
              >
                <Box width={'100%'}>
                  <Skeleton
                    variant="rounded"
                    width={'50%'}
                    height={16}
                    sx={{
                      mt: {xxs: 0, md: 1},
                      mb: 1,
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={'30%'}
                    height={12}
                    sx={{
                      mt: {xxs: 0, md: 1},
                    }}
                  />
                </Box>
                <Skeleton
                  variant="rounded"
                  width={100}
                  height={25}
                  sx={{
                    mt: {xxs: 0, md: 1},
                    ml: {xxs: 2, md: 'none'},
                    mb: 1,
                  }}
                />
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems={{xxs: 'flex-start', md: 'center'}}
                mb={{xxs: 5, md: 5}}
              >
                <Checkbox
                  id={`hide-resume`}
                  size={18}
                  sx={{...(!isLarge && {pt: 0.125})}}
                  name={`hide-resume`}
                  checked={!!hideResume}
                  data-testid="checkbox"
                  icon={
                    <Image
                      src={BlankTickHirist}
                      alt="unchecked"
                      width={18}
                      height={18}
                    />
                  }
                  onChange={() => setHideResume(hideResume ? 0 : 1)}
                  checkedIcon={
                    <Image
                      src={CheckHirist}
                      alt="checked"
                      width={18}
                      height={18}
                    />
                  }
                  label={
                    <Box ml={0.75}>
                      <Typography
                        sx={{
                          fontSize: {xxs: 14, md: 16},
                          whiteSpace: 'normal',
                          overflowWrap: 'break-word',
                          color: '#555',
                          cursor: 'pointer',
                        }}
                      >
                        Hide Profile from every recruiter / employer (Only
                        visible against positions you explicitly apply for)
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}

            {loading ? (
              <Box minWidth={'100%'} px={{xxs: 1, md: 2}}>
                <Skeleton
                  variant="rounded"
                  width={'50%'}
                  height={16}
                  sx={{
                    mt: 3,
                    mb: 1,
                  }}
                />
                {[
                  ...new Array(5).fill('').map(() => (
                    <>
                      <Skeleton
                        variant="rounded"
                        width={'100%'}
                        height={10}
                        sx={{
                          my: 0.5,
                        }}
                      />
                    </>
                  )),
                ]}
                <Skeleton
                  variant="rounded"
                  width={190}
                  height={16}
                  sx={{
                    mt: 3,
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={'100%'}
                  height={40}
                  sx={{
                    borderRadius: 16,
                    mt: 1,
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={122}
                  height={8}
                  sx={{
                    mt: 2.3,
                    mb: 4,
                  }}
                />
              </Box>
            ) : (
              <BlockCompaniesComponent
                isLarge={isLarge}
                setCurrentOrg={setCurrentOrg}
              />
            )}
          </Box>
        </Box>
      </Card>
      <Box display={'flex'} gap={1.5} mt={4} mb={2} ml={{xxs: 0, md: 1}}>
        <Button
          sx={{
            width: {xxs: '100%', md: '191px'},
            fontSize: '16px',
            backgroundColor: '#e9630c',
            borderRadius: '3px',
            px: 5.75,
            py: 1.375,
            height: '50px',
            '&:hover': {
              backgroundColor: '#e79e00',
            },
          }}
          variant="contained"
          type="submit"
          onClick={handleSaveSettings}
        >
          {buttonLoading ? (
            <Loader
              size={20}
              sx={{
                color: '#ffffff !important',
              }}
            />
          ) : (
            'Save Settings'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default PrivacySettingWidget;
